import React, { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { CheckCircleOutline, AccountBalanceWalletOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import AdBanner from '../components/AdBanner';

// Cloud Function base URL — injected via env or hardcoded for production
const FUNCTIONS_BASE = 'https://us-central1-finaculator.cloudfunctions.net';

function fmt(num) {
  return Number(num || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
}

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CustomerConsole() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    fetch(`${FUNCTIONS_BASE}/getPublicCustomerData?clientId=${encodeURIComponent(clientId)}`)
      .then(res => {
        if (!res.ok) throw new Error('Statement not found');
        return res.json();
      })
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [clientId]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0f1e' }}>
        <CircularProgress sx={{ color: '#15C358' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0f1e', gap: 2 }}>
        <Typography sx={{ color: '#ef5350', fontFamily: 'Inter', fontSize: 18, fontWeight: 600 }}>Statement Not Found</Typography>
        <Typography sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 14 }}>This link may be invalid or expired.</Typography>
      </Box>
    );
  }

  const { customer, accounts } = data;

  return (
    <Box sx={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, overflowY: 'auto', background: 'linear-gradient(160deg, #0a0f1e 0%, #0d1b35 100%)', pb: 8 }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, rgba(21,195,88,0.15), rgba(21,195,88,0.08))',
        borderBottom: '1px solid rgba(21,195,88,0.2)',
        px: { xs: 3, sm: 5 }, py: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            background: 'linear-gradient(135deg, #15C358, #109441)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 20, color: '#fff', fontFamily: 'Inter',
          }}>F</Box>
          <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.03em', color: '#f0f4ff', fontFamily: 'Inter' }}>
            Finaculator
          </Typography>
        </Box>
        <Chip
          label="Active Loan"
          size="small"
          sx={{ background: 'rgba(21, 195, 88,0.15)', color: '#15C358', border: '1px solid rgba(21, 195, 88,0.3)', fontFamily: 'Inter', fontWeight: 600, fontSize: 12 }}
        />
      </Box>

      <Box sx={{ maxWidth: 680, mx: 'auto', px: { xs: 2, sm: 4 }, pt: 4 }}>

        {/* ── Customer Info ── */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#f0f4ff', fontFamily: 'Inter', letterSpacing: '-0.02em' }}>
            {customer.name}
          </Typography>
          {customer.address && (
            <Typography sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 14, mt: 0.5 }}>
              {customer.address}
            </Typography>
          )}
          <Typography sx={{ color: '#64748b', fontSize: 12, fontFamily: 'Inter', mt: 1 }}>
            Statement generated on {fmtDate(new Date().toISOString())}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <AdBanner type="horizontal" />
        </Box>

        {/* ── Accounts ── */}
        {accounts.length === 0 && (
          <Typography sx={{ color: '#8892b0', fontFamily: 'Inter' }}>No active accounts found.</Typography>
        )}

        {accounts.map((account, idx) => {
          const paidAmount = account.entries
            ? Object.values(account.entries).reduce((s, e) => s + (e.isUpfrontInterest ? 0 : Number(e.amountPaid || 0)), 0)
            : 0;
          const paidDays = account.entries
            ? Object.values(account.entries).reduce((s, e) => s + (e.isUpfrontInterest ? 0 : Number(e.dayCount || 0)), 0)
            : 0;
          const balance = account.amount - paidAmount;
          const progress = account.installmentPeriod > 0
            ? Math.min(100, Math.round((paidDays / account.installmentPeriod) * 100))
            : 0;
          const shortId = account.accountId ? account.accountId.split('_')[0] : '—';

          return (
            <Box key={idx} sx={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 3,
              mb: 4,
              overflow: 'hidden',
            }}>
              {/* Account header */}
              <Box sx={{
                background: 'linear-gradient(135deg, rgba(21,195,88,0.12), rgba(21,195,88,0.06))',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                px: 3, py: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletOutlined sx={{ color: '#15C358', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#f0f4ff', fontFamily: 'Inter' }}>
                    A/C {shortId}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonthOutlined sx={{ color: '#64748b', fontSize: 16 }} />
                  <Typography sx={{ color: '#8892b0', fontSize: 13, fontFamily: 'Inter' }}>
                    Since {fmtDate(account.date)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: 3, py: 3 }}>
                {/* Summary rows */}
                {[
                  { label: 'Principal Amount', value: `₹ ${fmt(account.amount)}`, color: '#f0f4ff' },
                  { label: 'Interest', value: `₹ ${fmt(account.interestAmount)} (${account.interestPercentage}%)`, color: '#f0f4ff' },
                  { label: 'Installment', value: `₹ ${fmt(account.installmentAmount)} / ${account.installmentType || 'day'}`, color: '#f0f4ff' },
                  { label: 'Total Recovered', value: `₹ ${fmt(paidAmount)}`, color: '#0fd47a' },
                  { label: 'Outstanding Balance', value: `₹ ${fmt(balance)}`, color: balance > 0 ? '#ef5350' : '#0fd47a' },
                ].map(({ label, value, color }) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <Typography sx={{ color: '#8892b0', fontSize: 14, fontFamily: 'Inter' }}>{label}</Typography>
                    <Typography sx={{ color, fontSize: 14, fontWeight: 600, fontFamily: 'Inter' }}>{value}</Typography>
                  </Box>
                ))}

                {/* Progress bar */}
                <Box sx={{ mt: 3, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ color: '#8892b0', fontSize: 12, fontFamily: 'Inter' }}>Repayment Progress</Typography>
                    <Typography sx={{ color: '#15C358', fontSize: 12, fontWeight: 700, fontFamily: 'Inter' }}>{progress}%</Typography>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 10, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', width: `${progress}%`, borderRadius: 10,
                      background: 'linear-gradient(90deg, #15C358, #109441)',
                      transition: 'width 0.6s ease',
                    }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                    <Typography sx={{ color: '#8892b0', fontSize: 11, fontFamily: 'Inter' }}>{paidDays} {account.installmentType || 'days'} paid</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: 11, fontFamily: 'Inter' }}>{account.installmentPeriod} total</Typography>
                  </Box>
                </Box>

                {/* Ledger */}
                {account.entries && Object.keys(account.entries).length > 0 && (
                  <>
                    <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#f0f4ff', fontFamily: 'Inter', mb: 2 }}>
                      Payment History
                    </Typography>
                    <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: 'rgba(21,195,88,0.06)' }}>
                            <TableCell sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, py: 1 }}>Date</TableCell>
                            <TableCell align="center" sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, py: 1 }}>Installments</TableCell>
                            <TableCell align="right" sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, py: 1 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.values(account.entries)
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((entry, i) => (
                              <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 13, py: 0.75 }}>
                                  {entry.date?.split('-').reverse().join('-')}
                                </TableCell>
                                <TableCell align="center" sx={{ color: '#8892b0', fontFamily: 'Inter', fontSize: 13, py: 0.75 }}>
                                  {entry.isUpfrontInterest ? 'Upfront' : entry.dayCount}
                                </TableCell>
                                <TableCell align="right" sx={{ color: '#0fd47a', fontFamily: 'Inter', fontSize: 13, fontWeight: 600, py: 0.75 }}>
                                  ₹ {fmt(entry.amountPaid)}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            </Box>
          );
        })}

        <Box sx={{ mt: 4, mb: 4 }}>
          <AdBanner type="mobile" />
        </Box>

        {/* ── Footer ── */}
        <Box sx={{ textAlign: 'center', pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <CheckCircleOutline sx={{ fontSize: 14, color: '#15C358' }} />
            <Typography sx={{ color: '#64748b', fontSize: 12, fontFamily: 'Inter' }}>
              This is a digitally generated statement
            </Typography>
          </Box>
          <Typography sx={{ color: '#2d2d3f', fontSize: 11, fontFamily: 'Inter' }}>
            Powered by Finaculator · finaculator.web.app
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
